import { Link } from "@tanstack/react-router";
import type * as React from "react";
import { TrendArea } from "@/components/charts/TrendArea";
import { PageHeaderBar, PageHeaderMetric } from "@/components/layout/PageHeaderBar";
import { usePageHeader } from "@/components/layout/PageHeaderContext";
import { ContractTypeSelect } from "@/components/profitability/ContractTypeSelect";
import { benchmarkGap, MarginBadge } from "@/components/profitability/MarginBadge";
import { DataTable, ROW_CLASS } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { HeroPanel } from "@/components/shared/hero-panel";
import { InlineBar } from "@/components/shared/inline-bar";
import { KpiTile } from "@/components/shared/kpi-tile";
import { PanelTitle } from "@/components/shared/panel-title";
import { StatusChip } from "@/components/shared/status-chip";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useContractTypeParam } from "@/hooks/use-contract-type-param";
import { useRangeParam } from "@/hooks/use-range-param";
import { useSummary } from "@/hooks/use-summary";
import { COLUMN_HELP } from "@/lib/column-help";
import { hours, money, percent } from "@/lib/format";
import type { CompanySummary } from "@/lib/profitability-api";
import { boardHero, heroTone } from "./board-hero";

/** Publishes the company figure and the filters into the app header. */
function SummaryHeader({
  profit,
  marginPct,
  benchmark,
  rangeInfo,
  rangeLabel,
  range,
  onRangeChange,
  contractTypes,
  contractType,
  onContractTypeChange,
}: {
  profit: number;
  marginPct: number | null;
  benchmark: number | null;
  rangeInfo: CompanySummary["range"];
  rangeLabel: string;
  range: string;
  onRangeChange: (key: string) => void;
  contractTypes: string[] | undefined;
  contractType: string;
  onContractTypeChange: (key: string) => void;
}) {
  usePageHeader(
    () => (
      <PageHeaderBar
        eyebrow={<span className="text-xs text-muted-foreground">Nymbl</span>}
        title="Company profitability"
        fields={[
          {
            key: "profit",
            label: `Profit${rangeLabel ? ` · ${rangeLabel}` : ""}`,
            content: (
              <PageHeaderMetric
                value={money(profit)}
                badge={<MarginBadge value={marginPct} benchmark={benchmark} />}
              />
            ),
          },
        ]}
        filters={
          <ContractTypeSelect
            types={contractTypes}
            value={contractType}
            onChange={onContractTypeChange}
          />
        }
        range={rangeInfo}
        rangeValue={range}
        onRangeChange={onRangeChange}
      />
    ),
    [
      profit,
      marginPct,
      benchmark,
      rangeInfo,
      rangeLabel,
      range,
      onRangeChange,
      contractTypes,
      contractType,
      onContractTypeChange,
    ],
  );
  return null;
}

export function AccountBoard({
  title,
  subtitle,
  rows,
  benchmark,
  showGap = false,
  footnote,
  emptyDescription = "Nothing in the selected range.",
}: {
  title: string;
  subtitle: string;
  rows: CompanySummary["accounts_top"];
  benchmark: number | null;
  /**
   * Show the shortfall column. The worst board is RANKED on it, and a board
   * whose ordering column is off screen reads as broken: Radiology Imaging is
   * the firm's largest account and still tops it, which only makes sense once
   * the $339,623 it is short of the benchmark is visible beside the name.
   */
  showGap?: boolean;
  footnote?: React.ReactNode;
  emptyDescription?: string;
}) {
  // The top board ranks on profit dollars; its margin column is a plain bar so
  // the eye can compare rates down the list without reading each one. The
  // shortfall board is about being under benchmark, so there the chip's tone
  // is the point.
  const topMargin = Math.max(...rows.map((r) => r.margin_pct ?? 0), 1);
  return (
    <Card flush className="w-full">
      <CardHeader>
        <PanelTitle help={subtitle}>{title}</PanelTitle>
      </CardHeader>
      <CardContent className="p-0">
        <DataTable
          flush
          headers={
            showGap
              ? [
                  { label: "Account", help: COLUMN_HELP.account },
                  { label: "Below target", className: "text-right", help: COLUMN_HELP.belowTarget },
                  { label: "Margin", className: "text-right", help: COLUMN_HELP.margin },
                ]
              : [
                  { label: "Account", help: COLUMN_HELP.account },
                  { label: "Revenue", className: "text-right", help: COLUMN_HELP.revenue },
                  { label: "Profit", className: "text-right", help: COLUMN_HELP.profit },
                  { label: "Margin", className: "text-right", help: COLUMN_HELP.margin },
                ]
          }
          empty={<EmptyState title="No accounts" description={emptyDescription} />}
        >
          {rows.map((row) => (
            <tr key={row.account_id} className={ROW_CLASS}>
              <td className={"max-w-64 truncate px-5 py-2.5"}>
                <Link
                  to="/dashboard/accounts/$accountId"
                  params={{ accountId: row.account_id }}
                  className="font-semibold hover:underline"
                >
                  {row.account_name ?? row.account_id}
                </Link>
              </td>
              {showGap ? (
                <td className="px-5 py-2.5 text-right font-medium tabular-nums text-destructive">
                  {row.gap_vs_benchmark == null ? "—" : money(row.gap_vs_benchmark)}
                </td>
              ) : (
                <>
                  <td className="px-5 py-2.5 text-right tabular-nums">{money(row.revenue)}</td>
                  <td className="px-5 py-2.5 text-right font-medium tabular-nums">
                    {money(row.profit)}
                  </td>
                </>
              )}
              <td className="px-5 py-2.5 text-right">
                {showGap || row.margin_pct === null ? (
                  <MarginBadge value={row.margin_pct} benchmark={benchmark} />
                ) : (
                  <InlineBar
                    value={row.margin_pct / topMargin}
                    mode="plain"
                    width={72}
                    label={percent(row.margin_pct)}
                    className="justify-end"
                  />
                )}
              </td>
            </tr>
          ))}
        </DataTable>
        {footnote ? (
          <p className="border-t px-5 py-3 text-xs text-muted-foreground">{footnote}</p>
        ) : null}
      </CardContent>
    </Card>
  );
}

export function DashboardPage() {
  const [range, setRange] = useRangeParam();
  const [contractType, setContractType] = useContractTypeParam();
  const { data, isLoading, isError, error, refetch } = useSummary(range, contractType);

  if (isError) {
    return (
      <div className="w-full space-y-4">
        <ErrorState message={(error as Error)?.message} onRetry={() => refetch()} />
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="w-full space-y-4">
        <Skeleton className="h-40 w-full" />
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }

  const t = data.totals;
  const hero = boardHero(data);
  // The benchmark comparison MarginBadge already makes, not a new figure.
  const gap =
    data.benchmark_pct !== null && t.margin_pct !== null
      ? benchmarkGap(t.margin_pct, data.benchmark_pct)
      : null;

  const tone = heroTone(t.margin_pct, gap);

  return (
    <div className="w-full space-y-4">
      <SummaryHeader
        profit={t.profit}
        marginPct={t.margin_pct}
        benchmark={data.benchmark_pct}
        rangeInfo={data.range}
        rangeLabel={data.range?.label ?? ""}
        range={range}
        onRangeChange={setRange}
        contractTypes={data.contract_types}
        contractType={contractType}
        onContractTypeChange={setContractType}
      />

      <HeroPanel
        eyebrow={`Margin${data.range?.label ? ` · ${data.range.label}` : ""}`}
        figure={t.margin_pct === null ? "—" : `${t.margin_pct.toFixed(1)}%`}
        figureTone={tone.figure}
        status={
          gap === null || tone.chip === null ? undefined : (
            <StatusChip tone={tone.chip}>
              {gap.pts.toFixed(1)} pts {gap.direction} {data.benchmark_pct}% benchmark
            </StatusChip>
          )
        }
        chart={
          hero.trend ? <TrendArea points={hero.trend} benchmark={data.benchmark_pct} /> : undefined
        }
        // Month facts only. Revenue and cost sit in the tiles directly below;
        // repeating them here was the first thing noticed on a wide screen.
        facts={[
          ...(hero.bestMonth ? [{ label: "Best month", value: hero.bestMonth }] : []),
          ...(hero.lowestMonth ? [{ label: "Lowest month", value: hero.lowestMonth }] : []),
          ...(hero.belowBenchmark
            ? [{ label: "Months below benchmark", value: hero.belowBenchmark }]
            : []),
        ]}
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <KpiTile label="Revenue" value={money(t.revenue)} hint={`${t.accounts} accounts`} />
        <KpiTile label="Cost" value={money(t.cost)} hint={`${hours(t.hours)} hours`} />
        <KpiTile label="Profit" value={money(t.profit)} />
        <KpiTile
          label="Margin"
          value={t.margin_pct === null ? "—" : `${t.margin_pct.toFixed(1)}%`}
          hint={data.benchmark_pct !== null ? `Benchmark ${data.benchmark_pct}%` : undefined}
        />
        <KpiTile
          label="Hours"
          value={hours(t.hours)}
          hint={t.billable_hours !== undefined ? `${hours(t.billable_hours)} billable` : undefined}
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
        <AccountBoard
          title="Most profitable accounts"
          subtitle="Ranked by dollars of profit, not margin -- a 90% margin on $400 is not the account to look at."
          rows={data.accounts_top}
          benchmark={data.benchmark_pct}
        />
        {/* NOT the board above read backwards. Reversed, a profit-dollar
            ranking put nine $0-revenue accounts on a board captioned "least
            profitable" -- InclusiSync at $0 of revenue and $79,780 of cost
            above every client actually underperforming. */}
        <AccountBoard
          title="Biggest margin shortfall"
          subtitle={`Dollars of margin below the ${
            data.benchmark_pct ?? 55
          }% benchmark: revenue x benchmark, less actual profit. Ranked in dollars, so a bad percentage on a tiny account cannot outrank a six-figure shortfall. Accounts at or above benchmark are not listed.`}
          rows={data.accounts_bottom}
          benchmark={data.benchmark_pct}
          showGap
          emptyDescription="Every account with revenue in this range is at or above benchmark."
          footnote={
            data.accounts_unbilled && data.accounts_unbilled.count > 0 ? (
              <>
                Separately, {data.accounts_unbilled.count} accounts booked{" "}
                {money(data.accounts_unbilled.cost)} of cost with no revenue in this range --
                pre-sale, dormant or internal. Real cost, but not a margin signal, so they are not
                ranked here.
              </>
            ) : undefined
          }
        />
      </div>

      <Card flush className="w-full">
        <CardHeader>
          <PanelTitle help="Billable hours at each person's contracted rate, less the matching share of their cost. This is what their time is worth at the agreed rate, not what any client was invoiced -- and it is a read on rates and utilisation, not on the person.">
            People by profit
          </PanelTitle>
        </CardHeader>
        <CardContent className="p-0">
          <DataTable
            flush
            headers={[
              { label: "Person", help: COLUMN_HELP.person },
              { label: "Hours", className: "text-right", help: COLUMN_HELP.hours },
              { label: "Billable", className: "text-right", help: COLUMN_HELP.billableHours },
              { label: "Value at rate", className: "text-right", help: COLUMN_HELP.valueAtRate },
              // personCost, not cost: this column is scaled to the billable
              // share, and the account-level meaning read across would overstate
              // what sits behind Profit here.
              { label: "Cost", className: "text-right", help: COLUMN_HELP.personCost },
              { label: "Profit", className: "text-right", help: COLUMN_HELP.personProfit },
              { label: "Margin", className: "text-right", help: COLUMN_HELP.personMargin },
            ]}
            empty={
              <EmptyState
                title="No priced hours"
                description="Nobody booked billable hours with a rate in this range."
              />
            }
          >
            {data.people_top.map((row) => (
              <tr key={row.person} className={ROW_CLASS}>
                <td className="px-5 py-2.5 font-semibold">{row.person}</td>
                <td className="px-5 py-2.5 text-right tabular-nums">{hours(row.hours)}</td>
                <td className="px-5 py-2.5 text-right tabular-nums">{hours(row.billable_hours)}</td>
                <td className="px-5 py-2.5 text-right tabular-nums">{money(row.value)}</td>
                <td className="px-5 py-2.5 text-right tabular-nums">{money(row.cost)}</td>
                <td className="px-5 py-2.5 text-right font-medium tabular-nums">
                  {money(row.profit)}
                </td>
                <td className="px-5 py-2.5 text-right">
                  <MarginBadge value={row.margin_pct} benchmark={data.benchmark_pct} />
                </td>
              </tr>
            ))}
          </DataTable>
        </CardContent>
      </Card>
    </div>
  );
}
